// ApiTester.js - правильная обработка всех ошибок
class ApiTester {
  constructor() {
    this.logCallback = null;
    this.requestCount = 0;
    this.serviceUrls = {
      universe_gateway: 'http://localhost:4000',
      imcon: 'http://localhost:4001', 
      world_manager: 'http://localhost:4002',
      sim_character: 'http://localhost:4003',
      character_designer: 'http://localhost:4004'
    };
  }

  setLogCallback(callback) {
    this.logCallback = callback;
  }

  log(message, type = 'info', data = null) {
    if (this.logCallback) {
      this.logCallback(message, type, data);
    } else {
      console.log(`[${type.toUpperCase()}] ${message}`, data || '');
    }
  }

  // Универсальный безопасный fetch
  async safeFetch(url, options = {}) {
    const requestId = ++this.requestCount;
    const startTime = Date.now();
    let timeoutId;
    
    try {
      this.log(`📤 [${requestId}] Запрос: ${options.method || 'GET'} ${url}`, 'debug');

      const controller = new AbortController();
      
      // Таймаут через Promise.race
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          controller.abort();
          reject(new Error(`Таймаут запроса (${options.timeout || 8000}ms)`));
        }, options.timeout || 8000);
      });

      const fetchOptions = {
        method: options.method || 'GET',
        headers: {
          'Accept': 'application/json',
          ...options.headers
        },
        signal: controller.signal,
        mode: 'cors',
        credentials: 'omit'
      };

      if (options.body) {
        fetchOptions.body = options.body;
        if (!fetchOptions.headers['Content-Type']) {
          fetchOptions.headers['Content-Type'] = 'application/json';
        }
      }

      const fetchPromise = fetch(url, fetchOptions);
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      
      clearTimeout(timeoutId);

      const responseTime = Date.now() - startTime;

      if (!response.ok) {
        let errorBody = '';
        try {
          errorBody = await response.text();
        } catch (e) {
          errorBody = 'Не удалось прочитать тело ошибки';
        }
        
        const error = new Error(`HTTP ${response.status} - ${response.statusText}`);
        error.statusCode = response.status;
        error.responseBody = errorBody.substring(0, 200);
        throw error;
      }

      const contentType = response.headers.get('content-type') || '';
      let data;

      if (contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
        if (data.includes('<!DOCTYPE') || data.includes('<html')) {
          throw new Error(`Сервер вернул HTML вместо JSON`);
        }
      }

      this.log(`✅ [${requestId}] Успешный ответ (${responseTime}ms)`, 'debug', {
        status: response.status,
        responseTime
      });

      return {
        data,
        responseTime,
        status: response.status,
        headers: Object.fromEntries(response.headers.entries())
      };

    } catch (error) {
      // Всегда очищаем таймаут
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      const responseTime = Date.now() - startTime;
      
      // Унифицированная обработка ошибок
      let errorMessage = error.message;
      let errorType = 'unknown';
      
      if (error.name === 'AbortError' || error.message.includes('Таймаут')) {
        errorType = 'timeout';
        errorMessage = `Таймаут подключения (${options.timeout || 8000}ms)`;
      } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorType = 'connection_failed';
        errorMessage = `Не удалось установить соединение с сервером`;
      } else if (error.statusCode) {
        errorType = 'http_error';
        errorMessage = `HTTP ошибка ${error.statusCode}: ${error.responseBody || error.message}`;
      }
      
      this.log(`❌ [${requestId}] Ошибка запроса (${responseTime}ms): ${errorMessage}`, 'debug', {
        errorType,
        url,
        method: options.method || 'GET'
      });
      
      // Создаем стандартизированную ошибку
      const standardizedError = new Error(errorMessage);
      standardizedError.type = errorType;
      standardizedError.url = url;
      standardizedError.responseTime = responseTime;
      standardizedError.originalError = error;
      
      throw standardizedError;
    }
  }

  getServiceConfig(serviceKey) {
    const services = {
      universe_gateway: { 
        name: 'Universe Gateway',
        healthEndpoints: ['/api/health', '/status', '/']
      },
      imcon: { 
        name: 'ImCon',
        healthEndpoints: ['/api/health', '/status', '/']
      },
      world_manager: { 
        name: 'World Manager', 
        healthEndpoints: ['/api/health', '/status', '/']
      },
      sim_character: { 
        name: 'Sim Character',
        healthEndpoints: ['/api/health', '/status', '/']
      },
      character_designer: { 
        name: 'Character Designer',
        healthEndpoints: ['/api/health', '/status', '/']
      }
    };

    return services[serviceKey] || { 
      name: serviceKey, 
      healthEndpoints: [ '/api/health', '/status','/']
    };
  }

  // Универсальная проверка здоровья сервиса
  async checkServiceHealth(serviceKey) {
    const serviceConfig = this.getServiceConfig(serviceKey);
    const baseUrl = this.serviceUrls[serviceKey];
    
    if (!baseUrl) {
      return {
        status: 'error',
        error: 'Неизвестный URL сервиса',
        timestamp: new Date().toISOString(),
        message: 'Сервис не настроен в конфигурации'
      };
    }

    let serviceHealthy = false;
    let lastError = null;
    let successfulEndpoint = null;
    let responseTime = null;
    let attemptedEndpoints = [];

    this.log(`🔍 Проверка доступности ${serviceConfig.name}...`, 'info');

    // Последовательно проверяем все возможные endpoints
    for (const endpoint of serviceConfig.healthEndpoints) {
      try {
        const url = `${baseUrl}${endpoint}`;
        attemptedEndpoints.push(endpoint);
        const startTime = Date.now();

        const result = await this.safeFetch(url, { 
          timeout: 5000 
        });

        responseTime = Date.now() - startTime;
        serviceHealthy = true;
        successfulEndpoint = endpoint;

        this.log(`✅ ${serviceConfig.name} доступен через ${endpoint} (${responseTime}ms)`, 'success');
        break;

      } catch (error) {
        lastError = error;
        this.log(`⚠️ ${serviceConfig.name} недоступен через ${endpoint}: ${error.message}`, 'debug');
        // Продолжаем пробовать другие endpoints
      }
    }

    if (serviceHealthy) {
      return {
        status: 'healthy',
        responseTime,
        endpoint: successfulEndpoint,
        timestamp: new Date().toISOString(),
        url: baseUrl,
        message: 'Сервис доступен и отвечает'
      };
    } else {
      return {
        status: 'unhealthy',
        responseTime: null,
        endpoint: null,
        error: lastError?.message || 'Все endpoints недоступны',
        attemptedEndpoints,
        errorType: lastError?.type || 'unknown',
        timestamp: new Date().toISOString(),
        url: baseUrl,
        message: 'Сервис недоступен по всем проверенным endpoints'
      };
    }
  }

  async performHealthCheck(services) {
    const healthResults = {};
    const infrastructure = {
      rabbitmq: { status: 'unknown', message: 'Не проверено' },
      tarantool: { status: 'unknown', message: 'Не проверено' },
      mongodb: { status: 'unknown', message: 'Не проверено' },
      kratos_public: { status: 'unknown', message: 'Не проверено' },
      kratos_admin: { status: 'unknown', message: 'Не проверено' }
    };

    this.log('🏥 Запуск комплексной проверки сервисов...', 'info');

    // Параллельная проверка всех сервисов
    const healthPromises = services.map(async (service) => {
      healthResults[service.key] = await this.checkServiceHealth(service.key);
    });

    await Promise.allSettled(healthPromises);

    // Проверка инфраструктуры если ImCon доступен
    try {
      if (healthResults.imcon?.status === 'healthy') {
        this.log('🔍 Проверка состояния инфраструктуры...', 'info');
        
        const result = await this.safeFetch('http://localhost:4001/api/health/infrastructure', { 
          timeout: 15000 
        });
        
        if (result.data) {
          infrastructure.rabbitmq = { 
            ...result.data.rabbitmq, 
            status: result.data.rabbitmq?.status || 'unknown',
            timestamp: new Date().toISOString()
          };
          infrastructure.tarantool = { 
            ...result.data.tarantool, 
            status: result.data.tarantool?.status || 'unknown',
            timestamp: new Date().toISOString()
          };
          infrastructure.mongodb = { 
            ...result.data.mongodb, 
            status: result.data.mongodb?.status || 'unknown',
            timestamp: new Date().toISOString()
          };
          infrastructure.kratos_public = { 
            ...result.data.kratos_public, 
            status: result.data.kratos_public?.status || 'unknown',
            timestamp: new Date().toISOString()
          };
          infrastructure.kratos_admin = { 
            ...result.data.kratos_admin, 
            status: result.data.kratos_admin?.status || 'unknown',
            timestamp: new Date().toISOString()
          };
        }
        
        this.log('✅ Статус инфраструктуры получен', 'success');
      } else {
        this.log('ℹ️ ImCon недоступен, проверка инфраструктуры пропущена', 'info');
      }
    } catch (error) {
      this.log('⚠️ Ошибка при проверке инфраструктуры', 'warning', { 
        error: error.message 
      });
    }

    // Статистика результатов
    const healthyCount = Object.values(healthResults).filter(r => r.status === 'healthy').length;
    const unhealthyCount = Object.values(healthResults).filter(r => r.status === 'unhealthy').length;
    const errorCount = Object.values(healthResults).filter(r => r.status === 'error').length;
    const totalCount = Object.keys(healthResults).length;

    this.log('📊 Комплексная проверка завершена', 'info', {
      healthy: healthyCount,
      unhealthy: unhealthyCount,
      errors: errorCount,
      total: totalCount
    });

    return { healthResults, infrastructure };
  }

  // Методы проверки инфраструктуры
  async checkRabbitMQ() {
    try {
      this.log('🔍 Проверка состояния RabbitMQ...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/rabbitmq', { 
        timeout: 5000 
      });
      
      this.log('✅ Статус RabbitMQ получен', 'success');
      return { 
        status: result.data?.status || 'connected', 
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка проверки RabbitMQ', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        message: 'Не удалось проверить состояние RabbitMQ',
        timestamp: new Date().toISOString()
      };
    }
  }

  async checkTarantool() {
    try {
      this.log('🔍 Проверка состояния Tarantool...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/tarantool', { 
        timeout: 5000 
      });
      
      this.log('✅ Статус Tarantool получен', 'success');
      return { 
        status: result.data?.status || 'connected', 
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка проверки Tarantool', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        message: 'Не удалось проверить состояние Tarantool',
        timestamp: new Date().toISOString()
      };
    }
  }

  async checkMongoDB() {
    try {
      this.log('🔍 Проверка состояния MongoDB...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/mongodb', { 
        timeout: 5000 
      });
      
      this.log('✅ Статус MongoDB получен', 'success');
      return { 
        status: result.data?.status || 'connected', 
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка проверки MongoDB', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        message: 'Не удалось проверить состояние MongoDB',
        timestamp: new Date().toISOString()
      };
    }
  }

  // === KRATOS HEALTH CHECKS ===

  async checkKratos() {
    try {
      this.log('🔐 Проверка состояния Kratos Public API...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/kratos', { 
        timeout: 5000 
      });
      
      this.log('✅ Статус Kratos получен', 'success');
      return { 
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка проверки Kratos', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        message: 'Не удалось проверить состояние Kratos',
        timestamp: new Date().toISOString()
      };
    }
  }

  async checkKratosAdmin() {
    try {
      this.log('👑 Проверка состояния Kratos Admin API...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/kratos/admin', { 
        timeout: 5000 
      });
      
      this.log('✅ Статус Kratos Admin получен', 'success');
      return { 
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка проверки Kratos Admin', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        message: 'Не удалось проверить состояние Kratos Admin',
        timestamp: new Date().toISOString()
      };
    }
  }

  async checkKratosDetailed() {
    try {
      this.log('🔍 Детальная диагностика Kratos...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/kratos/detailed', { 
        timeout: 8000 
      });
      
      this.log('✅ Детальная информация Kratos получена', 'success');
      return {
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка детальной диагностики Kratos', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        timestamp: new Date().toISOString()
      };
    }
  }

  async getKratosUsers() {
    try {
      this.log('👥 Получение статистики пользователей Kratos...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/kratos/users', { 
        timeout: 5000 
      });
      
      this.log('✅ Статистика пользователей Kratos получена', 'success');
      return {
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка получения статистики пользователей Kratos', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        timestamp: new Date().toISOString()
      };
    }
  }

  // Универсальный метод отправки REST запросов
  async sendRestRequest(serviceKey, path, method = 'GET', body = null, customHeaders = {}) {
    const requestId = ++this.requestCount;
    const startTime = Date.now();
    
    try {
      const serviceConfig = this.getServiceConfig(serviceKey);
      const baseUrl = this.serviceUrls[serviceKey];
      
      if (!baseUrl) {
        throw new Error(`Сервис "${serviceKey}" не найден в конфигурации`);
      }

      // Нормализация пути
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${baseUrl}${normalizedPath}`;

      const options = {
        method: method.toUpperCase(),
        headers: {
          'Accept': 'application/json',
          ...customHeaders
        },
        timeout: 10000
      };

      if (method.toUpperCase() !== 'GET' && body) {
        options.body = typeof body === 'string' ? body : JSON.stringify(body);
        if (!options.headers['Content-Type']) {
          options.headers['Content-Type'] = 'application/json';
        }
      }

      this.log(`📤 [${requestId}] REST запрос: ${options.method} ${serviceConfig.name}`, 'info', {
        service: serviceKey,
        url,
        path: normalizedPath
      });

      const result = await this.safeFetch(url, options);
      const totalResponseTime = Date.now() - startTime;

      this.log(`✅ [${requestId}] Успешный ответ от ${serviceConfig.name} (${totalResponseTime}ms)`, 'success', {
        responseTime: totalResponseTime
      });

      return {
        data: result.data,
        responseTime: totalResponseTime,
        requestId,
        url,
        status: result.status,
        headers: result.headers
      };

    } catch (error) {
      const totalResponseTime = Date.now() - startTime;
      
      // Единообразное логирование ошибок
      this.log(`❌ [${requestId}] Ошибка запроса: ${error.message}`, 'error', {
        service: serviceKey,
        responseTime: totalResponseTime,
        errorType: error.type,
        method: method.toUpperCase()
      });
      
      // Стандартизированная ошибка
      const standardizedError = new Error(`Ошибка запроса к ${serviceKey}: ${error.message}`);
      standardizedError.service = serviceKey;
      standardizedError.url = error.url;
      standardizedError.method = method.toUpperCase();
      standardizedError.responseTime = totalResponseTime;
      standardizedError.errorType = error.type;
      standardizedError.originalError = error;
      
      throw standardizedError;
    }
  }

  // Детальные проверки инфраструктуры
  async checkRabbitMQDetailed() {
    try {
      this.log('🔍 Детальная диагностика RabbitMQ...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/rabbitmq/detailed', { 
        timeout: 8000 
      });
      
      this.log('✅ Детальная информация RabbitMQ получена', 'success');
      return {
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка детальной диагностики RabbitMQ', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        timestamp: new Date().toISOString()
      };
    }
  }

  async checkTarantoolDetailed() {
    try {
      this.log('🔍 Детальная диагностика Tarantool...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/tarantool/detailed', { 
        timeout: 8000 
      });
      
      this.log('✅ Детальная информация Tarantool получена', 'success');
      return {
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка детальной диагностики Tarantool', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        timestamp: new Date().toISOString()
      };
    }
  }

  async checkMongoDBDetailed() {
    try {
      this.log('🔍 Детальная диагностика MongoDB...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/mongodb/detailed', { 
        timeout: 8000 
      });
      
      this.log('✅ Детальная информация MongoDB получена', 'success');
      return {
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка детальной диагностики MongoDB', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        timestamp: new Date().toISOString()
      };
    }
  }

  async testRabbitMQPublish() {
    try {
      this.log('🧪 Тестирование публикации в RabbitMQ...', 'info');
      
      const result = await this.safeFetch('http://localhost:4001/api/health/rabbitmq/test', { 
        timeout: 10000 
      });
      
      this.log('✅ Тест публикации RabbitMQ выполнен', 'success');
      return {
        ...result.data,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.log('❌ Ошибка теста публикации RabbitMQ', 'error', { error: error.message });
      return { 
        status: 'error', 
        error: error.message,
        errorType: error.type,
        timestamp: new Date().toISOString()
      };
    }
  }

  // Вспомогательные методы
  async get(serviceKey, path, headers = {}) {
    return this.sendRestRequest(serviceKey, path, 'GET', null, headers);
  }

  async post(serviceKey, path, data, headers = {}) {
    return this.sendRestRequest(serviceKey, path, 'POST', data, headers);
  }

  async put(serviceKey, path, data, headers = {}) {
    return this.sendRestRequest(serviceKey, path, 'PUT', data, headers);
  }

  async delete(serviceKey, path, headers = {}) {
    return this.sendRestRequest(serviceKey, path, 'DELETE', null, headers);
  }

  // Проверка доступности сервиса
  async isServiceAvailable(serviceKey) {
    try {
      const health = await this.checkServiceHealth(serviceKey);
      return health.status === 'healthy';
    } catch (error) {
      return false;
    }
  }
}

// Создаем синглтон экземпляр
const apiTester = new ApiTester();
export default apiTester;